import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useGeoLocations, useCreateGeoLocation, useUpdateGeoLocation, useDeleteGeoLocation } from '../hooks/useMaster'
import { UserRole } from '@kfolks/types'
import { roleAtLeast } from '@/auth'
import { useAuth } from '@/hooks/useAuth'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/atoms/ui/table'
import { Button } from '@/shared/components/atoms/ui/button'
import { Input } from '@/shared/components/atoms/ui/input'
import { Badge } from '@/shared/components/atoms/ui/badge'
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from '@dnd-kit/core'
import {
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Icon } from '@/shared/components/atoms/Icon'

interface EditingRow {
	id: string
	code: string
	label: string
	countryCode?: string
	sortOrder: number
}

interface SortableRowProps {
	row: {
		geoLocationId: string
		code: string
		label: string
		countryCode?: string
		sortOrder: number
		isActive: boolean
	}
	isEditing: boolean
	editingData: EditingRow | null
	onEdit: (row: EditingRow) => void
	onSaveEdit: () => void
	onCancelEdit: () => void
	onDelete: (id: string) => void
	setEditingData: (data: EditingRow | null) => void
	updatePending: boolean
	deletePending: boolean
	isAdmin: boolean
}

function SortableRow({
	row,
	isEditing,
	editingData,
	onEdit,
	onSaveEdit,
	onCancelEdit,
	onDelete,
	setEditingData,
	updatePending,
	deletePending,
	isAdmin,
}: SortableRowProps) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: row.geoLocationId,
	})

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	}

	return (
		<TableRow ref={setNodeRef} style={style}>
			<TableCell className="w-12">
				<div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
					<Icon name="GripVertical" size="md" className="text-muted-foreground" />
				</div>
			</TableCell>
			<TableCell className="font-mono text-sm">
				{isEditing ? (
					<Input
						value={editingData?.code ?? ''}
						onChange={(e) => setEditingData(editingData ? { ...editingData, code: e.target.value } : null)}
						className="font-mono"
					/>
				) : (
					row.code
				)}
			</TableCell>
			<TableCell>
				{isEditing ? (
					<Input
						value={editingData?.label ?? ''}
						onChange={(e) => setEditingData(editingData ? { ...editingData, label: e.target.value } : null)}
					/>
				) : (
					row.label
				)}
			</TableCell>
			<TableCell>
				{isEditing ? (
					<Input
						value={editingData?.countryCode ?? ''}
						onChange={(e) => setEditingData(editingData ? { ...editingData, countryCode: e.target.value } : null)}
						className="font-mono uppercase"
						maxLength={2}
					/>
				) : (
					<span className="font-mono text-sm">{row.countryCode || '—'}</span>
				)}
			</TableCell>
			<TableCell>{row.isActive ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</TableCell>
			<TableCell className="text-right">
				{isAdmin && isEditing ? (
					<div className="flex items-center justify-end gap-1">
						<Button onClick={onSaveEdit} size="sm" disabled={updatePending} className="h-7 px-2 text-xs">
							Save
						</Button>
						<Button onClick={onCancelEdit} size="sm" variant="outline" className="h-7 px-2 text-xs">
							Cancel
						</Button>
					</div>
				) : isAdmin ? (
					<div className="flex items-center justify-end gap-1">
						<Button
							onClick={() =>
								onEdit({
									id: row.geoLocationId,
									code: row.code,
									label: row.label,
									countryCode: row.countryCode,
									sortOrder: row.sortOrder,
								})
							}
							size="sm"
							variant="outline"
							className="h-7 px-2 text-xs"
						>
							Edit
						</Button>
						<Button
							onClick={() => onDelete(row.geoLocationId)}
							size="sm"
							variant="destructive"
							disabled={deletePending}
							className="h-7 px-2 text-xs"
						>
							Delete
						</Button>
					</div>
				) : null}
			</TableCell>
		</TableRow>
	)
}

export function GeoLocationsTable() {
	const { user } = useAuth()
	const isSuperAdmin = roleAtLeast(user?.role, UserRole.SUPER_ADMIN)
	const queryClient = useQueryClient()
	const { data: geoLocations = [], isLoading, error } = useGeoLocations()
	const createMutation = useCreateGeoLocation()
	const updateMutation = useUpdateGeoLocation()
	const deleteMutation = useDeleteGeoLocation()

	const [editingId, setEditingId] = useState<string | null>(null)
	const [editingData, setEditingData] = useState<EditingRow | null>(null)
	const [isAdding, setIsAdding] = useState(false)
	const [newRow, setNewRow] = useState({ code: '', label: '', countryCode: '', sortOrder: 0 })

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	)

	const handleEdit = (row: EditingRow) => {
		setEditingId(row.id)
		setEditingData(row)
	}

	const handleCancelEdit = () => {
		setEditingId(null)
		setEditingData(null)
	}

	const handleSaveEdit = async () => {
		if (!editingData || !editingId) return

		try {
			await updateMutation.mutateAsync({
				id: editingId,
				data: {
					code: editingData.code,
					label: editingData.label,
					countryCode: editingData.countryCode || undefined,
					sortOrder: editingData.sortOrder,
				},
			})
			setEditingId(null)
			setEditingData(null)
		} catch {}
	}

	const handleDelete = async (id: string) => {
		if (!confirm('Are you sure you want to delete this geographic location?')) return

		try {
			await deleteMutation.mutateAsync(id)
		} catch {}
	}

	const handleAddNew = () => {
		setIsAdding(true)
		setNewRow({ code: '', label: '', countryCode: '', sortOrder: geoLocations.length })
	}

	const handleCancelAdd = () => {
		setIsAdding(false)
		setNewRow({ code: '', label: '', countryCode: '', sortOrder: 0 })
	}

	const handleSaveNew = async () => {
		if (!newRow.code || !newRow.label) {
			alert('Code and Label are required')
			return
		}

		try {
			await createMutation.mutateAsync({
				code: newRow.code,
				label: newRow.label,
				countryCode: newRow.countryCode || undefined,
				sortOrder: newRow.sortOrder,
			})
			setIsAdding(false)
			setNewRow({ code: '', label: '', countryCode: '', sortOrder: 0 })
		} catch {}
	}

	const handleDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event

		if (!over || active.id === over.id) return

		const sortedData = [...geoLocations].sort((a, b) => a.sortOrder - b.sortOrder)
		const oldIndex = sortedData.findIndex((item) => item.geoLocationId === active.id)
		const newIndex = sortedData.findIndex((item) => item.geoLocationId === over.id)

		if (oldIndex === -1 || newIndex === -1) return

		const reorderedItems = [...sortedData]
		const [movedItem] = reorderedItems.splice(oldIndex, 1)
		reorderedItems.splice(newIndex, 0, movedItem)

		const updatedItems = reorderedItems.map((item, index) => ({
			...item,
			sortOrder: index,
		}))

		// Optimistic update
		queryClient.setQueryData(['geo-locations'], updatedItems)

		try {
			await Promise.all(
				updatedItems.map((item) =>
					updateMutation.mutateAsync({
						id: item.geoLocationId,
						data: { sortOrder: item.sortOrder },
					}),
				),
			)
		} catch {
			await queryClient.invalidateQueries({ queryKey: ['geo-locations'] })
		}
	}

	if (isLoading) {
		return <div className="text-center py-8">Loading geographic locations...</div>
	}

	if (error) {
		return <div className="text-center py-8 text-red-600">Error loading geographic locations: {error.message}</div>
	}

	const sortedData = [...geoLocations].sort((a, b) => a.sortOrder - b.sortOrder)

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<p className="text-sm text-muted-foreground">{geoLocations.length} geographic locations</p>
				{isSuperAdmin && (
					<Button onClick={handleAddNew} disabled={isAdding} size="sm">
						Add Location
					</Button>
				)}
			</div>

			<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(event) => void handleDragEnd(event)}>
				<div className="border rounded-lg">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-12"></TableHead>
								<TableHead>Code</TableHead>
								<TableHead>Label</TableHead>
								<TableHead>Country Code</TableHead>
								<TableHead>Status</TableHead>
								<TableHead className="text-right">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{isAdding && (
								<TableRow>
									<TableCell></TableCell>
									<TableCell>
										<Input
											value={newRow.code}
											onChange={(e) => setNewRow({ ...newRow, code: e.target.value })}
											placeholder="CODE"
											className="font-mono"
										/>
									</TableCell>
									<TableCell>
										<Input
											value={newRow.label}
											onChange={(e) => setNewRow({ ...newRow, label: e.target.value })}
											placeholder="Display Label"
										/>
									</TableCell>
									<TableCell>
										<Input
											value={newRow.countryCode}
											onChange={(e) => setNewRow({ ...newRow, countryCode: e.target.value })}
											placeholder="ISO code"
											className="font-mono uppercase"
											maxLength={2}
										/>
									</TableCell>
									<TableCell>
										<Badge>Active</Badge>
									</TableCell>
									<TableCell className="text-right">
										<div className="flex items-center justify-end gap-1">
											<Button
												onClick={() => void handleSaveNew()}
												size="sm"
												disabled={createMutation.isPending}
												className="h-7 px-2 text-xs"
											>
												Save
											</Button>
											<Button onClick={handleCancelAdd} size="sm" variant="outline" className="h-7 px-2 text-xs">
												Cancel
											</Button>
										</div>
									</TableCell>
								</TableRow>
							)}

							<SortableContext
								items={sortedData.map((item) => item.geoLocationId)}
								strategy={verticalListSortingStrategy}
							>
								{sortedData.map((row) => {
									const isEditing = editingId === row.geoLocationId

									return (
										<SortableRow
											key={row.geoLocationId}
											row={row}
											isEditing={isEditing}
											editingData={editingData}
											onEdit={handleEdit}
											onSaveEdit={() => void handleSaveEdit()}
											onCancelEdit={handleCancelEdit}
											onDelete={(id: string) => void handleDelete(id)}
											setEditingData={setEditingData}
											updatePending={updateMutation.isPending}
											deletePending={deleteMutation.isPending}
											isAdmin={isSuperAdmin}
										/>
									)
								})}
							</SortableContext>

							{!isAdding && sortedData.length === 0 && (
								<TableRow>
									<TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
										No geographic locations found. Click "Add Location" to create one.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</DndContext>
		</div>
	)
}
